'use client';

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock, FaUser } from 'react-icons/fa';
import { usePermissions } from '../../context/PermissionContext';
// Added your logo import here
import SiteLogo from '../../assets/images/SparkLogistics.jpg';
import { BASE_URL } from '../../api';

const Login = () => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [userNameError, setUserNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { setMenuPermissions } = usePermissions();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUserNameError('');
    setPasswordError('');
    setError('');

    let isValid = true;
    if (!userName) {
      setUserNameError('Username is required*');
      isValid = false;
    }
    if (!password) {
      setPasswordError('Password is required*');
      isValid = false;
    }
    if (!isValid) return;

    setLoading(true);

    try {
      const currentTime = new Date().toISOString();
      const response = await fetch(
        `${BASE_URL}/api/superadmin/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userName,
            password,
            ipaddress: 'string',
            logintime: currentTime,
            logouttime: currentTime,
            loginstatus: 'active',
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('userId', data.data.id);
        localStorage.setItem('userName', data.data.name);
        const isCustomer = (data.data.role || '').toLowerCase() === 'customer' || data.data.menuAccess?.roleName === 'Customer';
        const role = isCustomer ? 'Customer' : (data.data.role || '');
        localStorage.setItem('role', role);

        if (isCustomer) {
          localStorage.setItem('customerId', data.data.id);
        } else {
          localStorage.removeItem('customerId');
        }

        const cleanPerms = {};
        if (data.data.menuAccess && data.data.menuAccess.menuPermissions && Object.keys(data.data.menuAccess.menuPermissions).length > 0) {
          Object.entries(data.data.menuAccess.menuPermissions).forEach(
            ([menu, actions]) => {
              cleanPerms[menu] = {
                view: !!actions.view,
                add: !!actions.add,
                edit: !!actions.edit,
                delete: !!actions.delete,
              };
            }
          );
          if (cleanPerms['Tracking Page']) {
            cleanPerms['Tracking No'] = { ...cleanPerms['Tracking Page'] };
          }
          if (cleanPerms['Tracking No']) {
            cleanPerms['Tracking Page'] = { ...cleanPerms['Tracking No'] };
          }
          if (cleanPerms['Route']) {
            cleanPerms['Route Master'] = { ...cleanPerms['Route'] };
          }
          if (cleanPerms['Route Master']) {
            cleanPerms['Route'] = { ...cleanPerms['Route Master'] };
          }
        } else {
          const defaultMenus = [
            'Dashboard', 'User Master', 'Role Master', 'Customer Master', 'Route Master',
            'Customer Price Setting', 'State Master', 'Area Master', 'Pincode Master',
            'Branch Master', 'Transport Master', 'Pick In', 'Booking', 'Outgoing Manifest',
            'Incoming Manifest', 'Runsheet', 'Docket Confirmation', 'Invoice', 'Collection',
            'Manifest Report', 'Runsheet Report', 'Delivery Report', 'MIS Report',
            'Tracking No', 'Customer Dashboard'
          ];
          defaultMenus.forEach(m => {
            cleanPerms[m] = { view: true, add: true, edit: true, delete: true };
          });
        }

        localStorage.setItem('menuPermissions', JSON.stringify(cleanPerms));
        setMenuPermissions(cleanPerms);

        if (isCustomer) {
          navigate('/customer-dashboard');
        } else {
          navigate('/Dashboard');
        }
      } else {
        setError(data.message || 'Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#f8fafc] relative overflow-hidden">
      {/* Background Decorative Blurs */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-50"></div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100 p-8 md:p-10">

          {/* Header with Logo */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <img
                src={SiteLogo}
                alt="Spark Logistics Logo"
                className="h-14 w-auto object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 tracking-tight">
              Welcome Back
            </h2>
            <p className="text-slate-500 mt-2 text-sm font-medium">
              Log in to manage your shipments
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-center">
              <span className="text-red-700 text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 ml-1">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <FaUser size={16} />
                </div>
                <input
                  type="text"
                  className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-2xl transition-all duration-200 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none ${userNameError ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'
                    }`}
                  placeholder="Enter username"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />
              </div>
              {userNameError && (
                <p className="text-red-500 text-xs mt-1.5 ml-1">{userNameError}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 ml-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <FaLock size={16} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`block w-full pl-11 pr-12 py-3.5 bg-slate-50 border rounded-2xl transition-all duration-200 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none ${passwordError ? 'border-red-400' : 'border-slate-200 focus:border-blue-500'
                    }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEye size={20} /> : <FaEyeSlash size={20} />}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-xs mt-1.5 ml-1">{passwordError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-slate-900 text-white font-bold rounded-2xl shadow-xl shadow-slate-200 hover:bg-slate-800 active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <div className="w-5 h-5 border-3 border-t-transparent border-white border-solid rounded-full animate-spin mr-3"></div>
                  <span>Authenticating...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-50 text-center">
            <p className="text-xs text-slate-400 font-medium">
              &copy; 2026 Vivekasoftek • Spark Logistics Management System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;